/**
 * GameObjectEngineContext — the concrete {@link IEngineContext} for the live runtime,
 * bound to one {@link GameObject}. Used by both the player and the editor (play-mode).
 *
 * For Phase 3 (no SoA flip yet) it backs the capability surface with the EXISTING sources
 * of truth: the runtime `Transform` (`go.transform`) for transform, the `core.SpriteRenderer`
 * component data for sprite, and injected providers for time / input / physics / spawn-destroy.
 * When the transform-on-SoA untangling lands, only the transform/world backing changes — the
 * contract and every script stay the same.
 */

import type { GameObject } from "../GameObject";
import type {
  ApiVersion,
  ComponentToken,
  EntityHandle,
  EventCaps,
  IEngineContext,
  InputCaps,
  LogCaps,
  PhysicsCaps,
  SpriteCaps,
  TimeCaps,
  TransformCaps,
  Vec2Like,
  WorldCaps,
} from "./IEngineContext";

const FALLBACK_LOG: LogCaps = {
  log: (...a) => console.log("[script]", ...a),
  warn: (...a) => console.warn("[script]", ...a),
  error: (...a) => console.error("[script]", ...a),
};
import type { EngineApi } from "./EngineApi.generated";
import { buildEngineApi } from "./ScriptApiRegistry";
import { isEngineComponent } from "./EngineComponent";
import { GpuScriptApi, GpuScope, type GpuBacking } from "./modules/GpuScriptApi";

export const GAMEOBJECT_API_VERSION: ApiVersion = { major: 0, minor: 1 };

/** Shared dependencies the host (player/editor/test) supplies once for all contexts. */
export interface GameObjectContextDeps {
  /** Resolve an entity handle to its GameObject (Level-backed at runtime). */
  resolve(entity: EntityHandle): GameObject | null;
  /** Frame timing (Engine.time at runtime). */
  time: TimeCaps;
  /** Input source (InputManager-backed at runtime). */
  input: InputCaps;
  /** Physics queries (optional). */
  physics?: PhysicsCaps;
  /** Pub/sub event bus (optional). */
  events?: EventCaps;
  /** Spawn/destroy (Level-backed; optional). */
  spawn?: () => EntityHandle;
  destroy?: (entity: EntityHandle) => void;
  /** All live entity handles (for `query`; optional). */
  entities?: () => Iterable<EntityHandle>;
  /** Resolve a GameObject GUID to its entity handle (Level.findByGuid-backed; optional). */
  resolveGuid?: (guid: string) => EntityHandle | null;
  /**
   * Diagnostics sink for `ctx.log` (optional). Hosts should back this with the engine Logger so
   * script output reaches engine-wide log capture (editor console, MCP `get_logs`); without it
   * the context falls back to raw `console.*`, which the engine log system does NOT see.
   */
  log?: LogCaps;
  /**
   * The global-services surface (`engine.*`), built once by the host with its real backings
   * (DI per runtime, §13.5). When provided it is authoritative; otherwise the context derives a
   * fallback `engine` from the per-cap deps above (back-compat / tests).
   */
  engine?: EngineApi;
  /**
   * GPU compute backing (optional). Unlike the session-shared `engine` surface, `engine.gpu` v2
   * is PER-SCRIPT scoped: each context overlays its own {@link GpuScriptApi} over a fresh
   * {@link GpuScope}, and `dispose()` (fired by ScriptHost on script destroy) frees exactly that
   * script's buffers/textures/kernels.
   */
  gpu?: GpuBacking;
}

const NOOP_PHYSICS: PhysicsCaps = { raycast: () => null, overlapCircle: () => [] };

export class GameObjectEngineContext implements IEngineContext {
  readonly apiVersion = GAMEOBJECT_API_VERSION;
  readonly entity: EntityHandle;

  readonly engine: EngineApi;
  readonly world: WorldCaps;
  readonly time: TimeCaps;
  readonly log: LogCaps;
  readonly transform: TransformCaps;
  readonly sprite: SpriteCaps;

  constructor(
    private readonly _go: GameObject,
    private readonly _deps: GameObjectContextDeps,
  ) {
    this.entity = _go.id;
    const go = _go;
    const deps = _deps;

    this.time = deps.time;
    this.log = deps.log ?? FALLBACK_LOG;

    // Transform — backed by the runtime Transform (local space), the current source of truth.
    const t = go.transform;
    this.transform = {
      getPosition: (): Vec2Like => ({ x: t.localPosition.x, y: t.localPosition.y }),
      setPosition: (x, y) => t.setLocalPosition(x, y, t.localPosition.z),
      translate: (dx, dy) => t.translate(dx, dy),
      getRotation: () => t.localRotation,
      setRotation: (deg) => {
        t.localRotation = deg;
      },
      rotate: (d) => t.rotate(d),
      getScale: (): Vec2Like => ({ x: t.localScale.x, y: t.localScale.y }),
      setScale: (x, y) => t.setLocalScale(x, y),
    };

    // Sprite — backed by the core.SpriteRenderer component data ({r,g,b,a} 0-255).
    this.sprite = {
      setColor: (rgb) => {
        const data = go.getComponentData("core.SpriteRenderer");
        if (data) {
          data.color.r = (rgb >> 16) & 0xff;
          data.color.g = (rgb >> 8) & 0xff;
          data.color.b = rgb & 0xff;
        }
      },
      setAlpha: (a) => {
        const data = go.getComponentData("core.SpriteRenderer");
        if (data) data.color.a = Math.max(0, Math.min(255, Math.round(a * 255)));
      },
    };

    this.world = {
      spawn: () => {
        if (!deps.spawn) throw new Error("[GameObjectEngineContext] spawn not available");
        return deps.spawn();
      },
      destroy: (e) => deps.destroy?.(e),
      getComponent: <T>(e: EntityHandle, token: ComponentToken<T>): T | null => {
        const c = deps.resolve(e)?.getComponent(token) ?? null;
        // API components (EngineComponent subclasses) get entity + engine bound so their methods
        // (e.g. RigidBody2D.applyForce) can reach the global services for THIS entity.
        if (isEngineComponent(c)) c.__bindView(e, this.engine);
        return c as T | null;
      },
      hasComponent: (e, token) => deps.resolve(e)?.hasComponent(token) ?? false,
      addComponent: <T>(e: EntityHandle, token: ComponentToken<T>): T => {
        const target = deps.resolve(e);
        if (!target) throw new Error(`[GameObjectEngineContext] unknown entity ${e}`);
        target.addComponent(token);
        return target.getComponent(token) as T;
      },
      removeComponent: (e, token) => deps.resolve(e)?.removeComponentByType(token.typeId) ?? false,
      query: function* (...tokens) {
        if (!deps.entities) return;
        for (const e of deps.entities()) {
          const target = deps.resolve(e);
          if (target && tokens.every((t) => target.hasComponent(t))) yield e;
        }
      },
      getEntityByGuid: (guid) => deps.resolveGuid?.(guid) ?? null,
      getTransform: (e) => {
        const target = deps.resolve(e);
        if (!target) return null;
        const tr = target.transform;
        // World-space position (matches how camera/AI logic reads other entities); rotation/
        // scale are local (world rot/scale would need matrix decomposition — add when needed).
        return {
          position: { x: tr.position.x, y: tr.position.y },
          rotation: tr.localRotation,
          scale: { x: tr.localScale.x, y: tr.localScale.y },
        };
      },
      setPosition: (e, x, y) => {
        const tr = deps.resolve(e)?.transform;
        if (tr) tr.setLocalPosition(x, y, tr.localPosition.z);
      },
      setRotation: (e, deg) => {
        const tr = deps.resolve(e)?.transform;
        if (tr) tr.localRotation = deg;
      },
      setScale: (e, sx, sy) => {
        deps.resolve(e)?.transform.setLocalScale(sx, sy);
      },
    };

    // Global-services surface (§2/§6). Prefer the host-built `engine` (real backings incl. a
    // force-capable PhysicsBacking, §13.5); otherwise derive a fallback from the per-cap deps
    // (back-compat / tests) — there the mutating physics methods stay no-ops.
    const engineBase =
      deps.engine ??
      buildEngineApi({
        physics: deps.physics ?? NOOP_PHYSICS,
        events: deps.events,
        input: deps.input,
        scene: deps.spawn
          ? { spawn: () => deps.spawn!(), destroy: (e) => deps.destroy?.(e) }
          : undefined,
      });

    // engine.gpu is per-script scoped (v2): overlay THIS context's GpuScriptApi over a fresh
    // scope so dispose() frees exactly this script's GPU resources (W-GPU lifecycle contract).
    if (deps.gpu) {
      this._gpuScope = new GpuScope(deps.gpu, go.name || `entity:${this.entity}`, this.log);
      this.engine = { ...engineBase, gpu: new GpuScriptApi(this._gpuScope) };
    } else {
      this.engine = engineBase;
    }
  }

  private _gpuScope: GpuScope | null = null;

  /** Free per-script scoped resources (ScriptHost fires this after onDestroy). Idempotent. */
  dispose(): void {
    this._gpuScope?.dispose();
  }
}
